import type { PermissionKey, PublicUser } from '@otter/shared';
import { hasPermission, isPersonalOwner } from '../lib/perm';
import { OtterMark } from './OtterMark';
import type { View } from '../App';

declare const __APP_VERSION__: string;

type NavEntry = { id: View; label: string; glyph: string; perm: PermissionKey | null };

const GROUPS: Array<{ title: string; entries: NavEntry[] }> = [
  {
    title: 'operativa',
    entries: [
      { id: 'dash', label: 'Dashboard', glyph: '◐', perm: 'dashboard.view' },
      { id: 'cli', label: 'Clientes', glyph: '◇', perm: 'clients.view' },
      { id: 'em', label: 'Facturas emitidas', glyph: '↗', perm: 'invoicesIssued.view' },
      { id: 're', label: 'Facturas recibidas', glyph: '↙', perm: 'invoicesReceived.view' },
    ],
  },
  {
    title: 'finanzas',
    entries: [
      { id: 'tes', label: 'Tesorería', glyph: '$', perm: 'treasury.view' },
      { id: 'rep', label: 'Reportes', glyph: '◰', perm: 'reports.view' },
    ],
  },
  {
    title: 'comercial',
    entries: [{ id: 'pric', label: 'Calculadora', glyph: '∿', perm: 'pricing.view' }],
  },
  {
    title: 'firmas',
    entries: [{ id: 'sig', label: 'Firmas', glyph: '✎', perm: 'signatures.view' }],
  },
  {
    // Private personal-finance module. The whole group only renders for the
    // single owner (system admin + code 'MV'); see the filter below.
    title: 'personal',
    entries: [{ id: 'pf', label: 'Personal', glyph: '◈', perm: null }],
  },
  {
    title: 'sistema',
    // Admin entrance is reserved for the system admin. Per-permission admin
    // sub-tabs filter further inside the Admin view.
    entries: [{ id: 'ad', label: 'Administración', glyph: '✦', perm: null }],
  },
];

type Props = {
  user: PublicUser;
  view: View;
  onView: (v: View) => void;
};

export function Sidebar({ user, view, onView }: Props) {
  const groups = GROUPS.map((g) => ({
    ...g,
    entries: g.entries.filter((entry) => {
      if (entry.id === 'pf') return isPersonalOwner(user);
      if (entry.id === 'ad') return user.isSystemAdmin || hasPermission(user, 'admin.users.manage') || hasPermission(user, 'admin.roles.manage');
      return entry.perm === null || hasPermission(user, entry.perm);
    }),
  })).filter((g) => g.entries.length > 0);

  return (
    <aside className="sb">
      <div className="logo">
        <OtterMark />
        <div className="name">OTTER</div>
        <div className="ver">v{__APP_VERSION__}</div>
      </div>
      {groups.map((g) => (
        <div key={g.title}>
          <div className="grp">{g.title}</div>
          <nav>
            {g.entries.map((n) => (
              <a
                key={n.id}
                className={view === n.id ? 'active' : ''}
                onClick={(e) => {
                  e.preventDefault();
                  onView(n.id);
                }}
                href="#"
              >
                <span className="g">{n.glyph}</span>
                {n.label}
              </a>
            ))}
          </nav>
        </div>
      ))}
      <div className="sidefoot">
        <div className="ln">
          <span>build</span>
          <b>v{__APP_VERSION__}</b>
        </div>
        <div className="ln">
          <span>api</span>
          <b className="ok">ok</b>
        </div>
        <a className="powered" href="https://usenorlia.com" target="_blank" rel="noreferrer">
          powered by <b>Norlia</b>
        </a>
      </div>
    </aside>
  );
}
